import { ReactElement, useMemo } from 'react';
import loadable from '@loadable/component';
import { useCouponCodeCatcher } from '@blocs.auth';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import { defineRoutes, RenderRoutes } from '@blocs.router';
import Loader from '@minecraft.loader';
import { useRemoveSplashLoader } from '@minecraft.utils';
import ProfileRedirect from '@consumer-talent.components/ProfileRedirect';
import RootRedirect from '@consumer-talent.components/RootRedirect';
import RouteAuthenticator from '@consumer-talent.components/RouteAuthenticator';

const loaderFallback = { fallback: <Loader /> };

const Login = loadable(
  () => import(/* webpackChunkName: "talent-login" */ '@consumer-talent.components/Login'),
  loaderFallback
);
const TalentsCreate = loadable(
  () => import(/* webpackChunkName: "talent-create" */ '@consumer-talent.components/TalentsCreate'),
  loaderFallback
);
const TalentDetail = loadable(
  () => import(/* webpackChunkName: "talent-detail" */ '@consumer-talent.components/TalentDetail'),
  loaderFallback
);
const TalentsCopy = loadable(
  () => import(/* webpackChunkName: "talent-copy" */ '@consumer-talent.components/TalentsCopy'),
  loaderFallback
);
const TalentsCopySelect = loadable(
  () => import(/* webpackChunkName: "talent-copy-select" */ '@consumer-talent.components/TalentsCopySelect'),
  loaderFallback
);
const TalentsName = loadable(
  () => import(/* webpackChunkName: "talent-name" */ '@consumer-talent.components/TalentsName'),
  loaderFallback
);
const TalentsSelect = loadable(
  () => import(/* webpackChunkName: "talent-talents" */ '@consumer-talent.components/TalentsSelect'),
  loaderFallback
);
const Project = loadable(
  () => import(/* webpackChunkName: "talent-project" */ '@consumer-talent.components/Project'),
  loaderFallback
);
const CustomizeSubmission = loadable(
  () =>
    import(/* webpackChunkName: "talent-customize-submission" */ '@consumer-talent.components/customize-submission'),
  loaderFallback
);
const Role = loadable(
  () => import(/* webpackChunkName: "talent-role" */ '@consumer-talent.components/Role'),
  loaderFallback
);
const Request = loadable(
  () => import(/* webpackChunkName: "talent-request" */ '@consumer-talent.components/Request/AskQuestion'),
  loaderFallback
);
const MediaRequest = loadable(
  () => import(/* webpackChunkName: "talent-media-request" */ '@consumer-talent.components/MediaRequest'),
  loaderFallback
);
const AccountDetails = loadable(
  () => import(/* webpackChunkName: "talent-account-details" */ '@consumer-talent.components/Accounts/AccountDetails'),
  loaderFallback
);
const ProfileCreate = loadable(
  () => import(/* webpackChunkName: "talent-profile-create" */ '@consumer-talent.components/Profile/ProfileCreate'),
  loaderFallback
);
const ProfileSkipStep = loadable(
  () =>
    import(/* webpackChunkName: "talent-skip-step" */ '@consumer-talent.components/Profile/ProfileCreate/skip-step'),
  loaderFallback
);
const ProfileSettings = loadable(
  () => import(/* webpackChunkName: "talent-profile-settings" */ '@consumer-talent.components/Profile/Settings'),
  loaderFallback
);
const ProfileSettingsProfiles = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-settings-profiles" */ '@consumer-talent.components/Profile/Settings/ProfileSettingsProfilesRoute'
    ),
  loaderFallback
);
const ProfileSettingsArtists = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-settings-artists" */ '@consumer-talent.components/Profile/Settings/ProfileSettingsArtistsRoute'
    ),
  loaderFallback
);
const ProfileEdit = loadable(
  () => import(/* webpackChunkName: "talent-profile-edit" */ '@consumer-talent.components/Profile/ProfileEdit/edit'),
  loaderFallback
);
const ProfilePreview = loadable(
  () =>
    import(/* webpackChunkName: "talent-profile-preview" */ '@consumer-talent.components/Profile/ProfileView/preview'),
  loaderFallback
);
const RosterInvite = loadable(
  () =>
    import(/* webpackChunkName: "talent-roster-invite" */ '@consumer-talent.components/RosterInvite/RosterInviteRoute'),
  loaderFallback
);
const AcceptProfileAccess = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-accept-profile-access" */ '@consumer-talent.components/AcceptProfileAccess/AcceptProfileAccessRoute'
    ),
  loaderFallback
);
const ArtistsCreate = loadable(
  () => import(/* webpackChunkName: "talent-artists-create" */ '@consumer-talent.components/Artists/ArtistCreateRoute'),
  loaderFallback
);
const ArtistsCreatePersonal = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-artists-create-personal" */ '@consumer-talent.components/Artists/CreatePersonalRoute'
    ),
  loaderFallback
);
const ArtistsDetail = loadable(
  () => import(/* webpackChunkName: "talent-artists-select" */ '@consumer-talent.components/Artists/ArtistDetailRoute'),
  loaderFallback
);
const ArtistsProfiles = loadable(
  () =>
    import(/* webpackChunkName: "talent-artists-profiles" */ '@consumer-talent.components/Artists/ArtistProfilesRoute'),
  loaderFallback
);
const ArtistsSelect = loadable(
  () => import(/* webpackChunkName: "talent-artists" */ '@consumer-talent.components/Artists/ArtistsSelectRoute'),
  loaderFallback
);
const ScheduleInvite = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-schedule-invite" */ '@consumer-talent.components/Request/ScheduleInvite/ScheduleInvite'
    ),
  loaderFallback
);
const NotificationSettingsArtist = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-notif-settings-artist" */ '@consumer-talent.components/Accounts/NotificationSettings/Artist'
    ),
  loaderFallback
);
const NotificationSettingsProjects = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-notif-settings-projects" */ '@consumer-talent.components/Accounts/NotificationSettings/ProjectsRoute'
    ),
  loaderFallback
);
const NotificationSettings = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-notif-settings" */ '@consumer-talent.components/Accounts/NotificationSettings/NotificationSettingsRoute'
    ),
  loaderFallback
);
const MediaBank = loadable(
  () => import(/* webpackChunkName: "talent-media-bank" */ '@consumer-talent.components/Media/MediaBankRoute'),
  loaderFallback
);
const CastingBillboard = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-billboard" */ '@consumer-talent.components/casting-billboard/CastingBillboardRoute'
    ),
  loaderFallback
);
const SavedSearches = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-saved-searches" */ '@consumer-talent.components/casting-billboard/SavedSearchesRoute'
    ),
  loaderFallback
);
const EmailReset = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-email-reset" */ '@consumer-talent.components/Accounts/AccountEmail/EmailResetRoute'
    ),
  loaderFallback
);
const ResetEmail = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-reset-email" */ '@consumer-talent.components/Accounts/AccountEmail/ResetEmailRoute'
    ),
  loaderFallback
);
const Submissions = loadable(
  () => import(/* webpackChunkName: "talent-submissions" */ '@consumer-talent.components/Submissions/SubmissionsRoute'),
  loaderFallback
);
const VerifyEmail = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-verify-email" */ '@consumer-talent.components/Accounts/AccountEmail/VerifyEmail'
    ),
  loaderFallback
);
const YourAlerts = loadable(
  () => import(/* webpackChunkName: "talent-your-alerts" */ '@consumer-talent.components/Alerts/YourAlertsRoute'),
  loaderFallback
);
const HeadshotsRoute = loadable(
  () => import(/* webpackChunkName: "talent-headshots" */ '@consumer-talent.components/Headshots/HeadshotsRoute'),
  loaderFallback
);
const YourAuditions = loadable(
  () => import(/* webpackChunkName: "your-insights" */ 'components/Insights/YourAuditions'),
  loaderFallback
);
const PublicProfile = loadable(
  () => import(/* webpackChunkName: "talent-public-profile" */ '@consumer-talent.components/PublicProfile')
);
const EditUploadedPhoto = loadable(
  () => import(/* webpackChunkName: "talent-edit-uploaded-photo" */ '@consumer-talent.components/EditUploadedPhoto'),
  loaderFallback
);
const MembershipOverview = loadable(
  () => import(/* webpackChunkName: "talent-membership" */ '@consumer-talent.components/MembershipAndBilling/Overview'),
  loaderFallback
);
const MembershipPaywall = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-membership-paywall" */ '@consumer-talent.components/MembershipAndBilling/MembershipPaywallRoute'
    ),
  loaderFallback
);
const SignupPaywall = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-signup-paywall" */ '@consumer-talent.components/MembershipAndBilling/SignupPaywallRoute'
    ),
  loaderFallback
);
const EditBillingInfo = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-edit-billing-info" */ '@consumer-talent.components/MembershipAndBilling/EditBillingInfo'
    ),
  loaderFallback
);
const DefaultRedirect = loadable(
  () => import(/* webpackChunkName: "talent-default-redirect" */ '@consumer-talent.components/DefaultRedirect')
);
const Maintenance = loadable(
  () => import(/* webpackChunkName: "talent-maintenance" */ '@consumer-talent.components/Maintenance'),
  loaderFallback
);
const SubmissionTrends = loadable(
  () => import(/* webpackChunkName: "submission-trends" */ 'components/Insights/SubmissionTrends')
);
const RoleTracker = loadable(() => import(/* webpackChunkName: "role-tracker" */ 'components/Insights/RoleTracker'));
const InsightsRedirect = loadable(
  () =>
    import(
      /* webpackChunkName: "talent-insights-redirect" */ '@consumer-talent.components/Insights/InsightsRedirectRoute'
    ),
  loaderFallback
);
const SubmissionCompletion = loadable(
  () =>
    import(/* webpackChunkName: "talent-submission-completion" */ '@consumer-talent.components/SubmissionCompletion'),
  loaderFallback
);
const NotFound = loadable(
  () => import(/* webpackChunkName: "talent-not-found" */ '@consumer-talent.components/NotFound'),
  loaderFallback
);

export const appRoutes = defineRoutes([
  { key: 'publicProfile', path: '/public-profile/:guid', exact: true, Page: PublicProfile },
  { key: 'maintenance', path: '/maintenance', exact: true, Page: Maintenance },
  { key: 'authenticate', path: '/authenticate/:refreshToken', exact: true, Page: RouteAuthenticator },
  { key: 'profileRedirect', path: '/profile', exact: true, Page: ProfileRedirect },
  { key: 'defaultRedirect', path: '/default-redirect', exact: true, Page: DefaultRedirect },
  { key: 'root', path: '/', exact: true, Page: RootRedirect },
  { key: 'login', path: '/login', exact: true, Page: Login },
  { key: 'talentCreate', path: '/talents/create', exact: true, Page: TalentsCreate },
  { key: 'talentDetail', path: '/talents/:artistId', exact: true, Page: TalentDetail },
  { key: 'talentCopy', path: '/profiles/:profileId/copy', exact: true, Page: TalentsCopy },
  {
    key: 'talentCopyFromRole',
    path: '/project/:projectId/role/:roleId/profiles/:profileId/copy',
    exact: true,
    Page: TalentsCopy,
  },
  { key: 'talentCopySelect', path: '/profiles/:profileId/copy-select', exact: true, Page: TalentsCopySelect },
  {
    key: 'talentCopySelectFromRole',
    path: '/project/:projectId/role/:roleId/profiles/:profileId/copy-select',
    exact: true,
    Page: TalentsCopySelect,
  },
  { key: 'talentName', path: '/talents/:artistId/name', exact: true, Page: TalentsName },
  { key: 'talents', path: '/talents', exact: true, Page: TalentsSelect },
  { key: 'project', path: '/project/:projectId', exact: true, Page: Project },
  {
    key: 'customizeSubmission',
    path: '/project/:projectId/role/:roleId/customize-submission',
    exact: true,
    Page: CustomizeSubmission,
  },
  {
    key: 'submissionCompletion',
    path: '/project/:projectId/role/:roleId/submission-completion',
    exact: true,
    Page: SubmissionCompletion,
  },
  { key: 'role', path: '/project/:projectId/role/:roleId', exact: true, Page: Role },
  { key: 'request', path: '/request/:requestId', exact: true, Page: Request },
  { key: 'mediaRequest', path: '/media-request/:requestMediaId', exact: true, Page: MediaRequest },
  { key: 'accountDetails', path: '/account-settings/details', exact: true, Page: AccountDetails },
  {
    key: 'notificationSettingsArtist',
    path: '/account-settings/notifications/:artistId',
    exact: true,
    Page: NotificationSettingsArtist,
  },
  {
    key: 'notificationSettingsProjects',
    path: '/account-settings/notifications/:artistId/projects',
    exact: true,
    Page: NotificationSettingsProjects,
  },
  { key: 'notificationSettings', path: '/account-settings/notifications', exact: true, Page: NotificationSettings },
  { key: 'profileVerifyAge', path: '/profile/verify-age', exact: true, Page: ProfileCreate },
  { key: 'profileCreate', path: '/profile/create', exact: true, Page: ProfileCreate },
  { key: 'profileCreateWithId', path: '/profile/create/:profileId', exact: true, Page: ProfileCreate },
  { key: 'profileUploadPhoto', path: '/profile/:profileId/upload-photo', exact: true, Page: ProfileSkipStep },
  {
    key: 'profileEditPrimaryPhoto',
    path: '/profile/:profileId/edit-primary-photo',
    exact: true,
    Page: ProfileSkipStep,
  },
  { key: 'profileEditThumbnail', path: '/profile/:profileId/edit-thumbnail', exact: true, Page: ProfileSkipStep },
  { key: 'profileUploadVideo', path: '/profile/:profileId/upload-video', exact: true, Page: ProfileSkipStep },
  { key: 'profileUploadAudio', path: '/profile/:profileId/upload-audio', exact: true, Page: ProfileSkipStep },
  {
    key: 'profileSettings',
    path: '/profile-settings/talents/:artistId/profiles/:profileId',
    exact: true,
    Page: ProfileSettings,
  },
  { key: 'membershipOverview', path: '/membership-billing', exact: true, Page: MembershipOverview },
  { key: 'editBillingInfo', path: '/membership-billing/edit-billing-info', exact: true, Page: EditBillingInfo },
  {
    key: 'membershipSubscriptionSummary',
    path: '/membership-billing/:artistId/subscription-summary/:planId',
    exact: true,
    Page: MembershipPaywall,
  },
  {
    key: 'membershipCheckout',
    path: '/membership-billing/:artistId/checkout',
    exact: true,
    Page: MembershipPaywall,
  },
  {
    key: 'membershipPurchaseSuccess',
    path: '/membership-billing/:artistId/purchase-success',
    exact: true,
    Page: MembershipPaywall,
  },
  {
    key: 'profileSettingsProfiles',
    path: '/profile-settings/talents/:artistId/profiles',
    exact: true,
    Page: ProfileSettingsProfiles,
  },
  { key: 'profileSettingsArtists', path: '/profile-settings/talents', exact: true, Page: ProfileSettingsArtists },
  { key: 'profileSubscriptions', path: '/profile/:profileId/subscriptions', exact: true, Page: SignupPaywall },
  { key: 'profilePurchaseSuccess', path: '/profile/:profileId/purchase-success', exact: true, Page: SignupPaywall },
  {
    key: 'profileSubscriptionSummary',
    path: '/profile/:profileId/subscriptions/:planId',
    exact: true,
    Page: SignupPaywall,
  },
  { key: 'profileCheckout', path: '/profile/:profileId/checkout', exact: true, Page: SignupPaywall },
  { key: 'profileEdit', path: '/profile/:profileId/:editView', exact: true, Page: ProfileEdit },
  { key: 'profile', path: '/profile/:profileId', exact: true, Page: ProfilePreview },
  // /roster-invite/:token is the route for a user who gets a roster invite email and clicks "I have an existing account" link
  { key: 'rosterInvite', path: '/roster-invite/:token', exact: true, Page: RosterInvite },
  /*
    /roster-invite/:token/:register is the route for a user who gets a roster invite email and clicks "I need to create an account" link
    this route will try to log them in if they actually do already have an account, if they do not they go to the
    login app at /login/signup/TALENT which is the same for as users who sign up directly
  */
  { key: 'rosterInviteRegister', path: '/roster-invite/:token/:register', exact: true, Page: RosterInvite },
  { key: 'acceptProfileAccess', path: '/accept-profile-access/:token', exact: true, Page: AcceptProfileAccess },
  {
    key: 'acceptProfileAccessRegister',
    path: '/accept-profile-access/:token/:register',
    exact: true,
    Page: AcceptProfileAccess,
  },
  // create personal is where a user is sent after creating a repped profile or accepting a repped profile on a fresh artist
  { key: 'artistCreatePersonal', path: '/artists/create-personal', exact: true, Page: ArtistsCreatePersonal },
  { key: 'artistCreate', path: '/artists/create', exact: true, Page: ArtistsCreate },
  // when a user is accepting a roster invite to an existing account
  // this page is where they select whether to create a new profile or copy
  // an existing one for the new repped profile
  { key: 'artistDetail', path: '/artists/:artistId', exact: true, Page: ArtistsDetail },
  { key: 'artistProfiles', path: '/artists/:artistId/profiles', exact: true, Page: ArtistsProfiles },
  { key: 'artists', path: '/artists', exact: true, Page: ArtistsSelect },
  { key: 'auditionInvite', path: '/audition-invite/:requestId', exact: true, Page: ScheduleInvite },
  { key: 'callbackInvite', path: '/callback-invite/:requestId', exact: true, Page: ScheduleInvite },
  { key: 'mediaBank', path: '/media-bank', exact: true, Page: MediaBank },
  { key: 'mediaBankType', path: '/media-bank/:mediaType', exact: true, Page: MediaBank },
  { key: 'savedSearches', path: '/casting-billboard/saved-searches', exact: true, Page: SavedSearches },
  {
    key: 'castingBillboard',
    path: '/casting-billboard/:subNav',
    optionalParams: ['subNav'],
    Page: CastingBillboard,
  },
  { key: 'emailReset', path: '/email-reset', exact: true, Page: EmailReset },
  { key: 'resetEmail', path: '/reset-email', exact: true, Page: ResetEmail },
  { key: 'submissions', path: '/submissions', exact: true, Page: Submissions },
  { key: 'verifyEmail', path: '/verify-email', exact: true, Page: VerifyEmail },
  { key: 'yourAlerts', path: '/your-alerts', exact: true, Page: YourAlerts },
  { key: 'headshots', path: '/headshots', exact: true, Page: HeadshotsRoute },
  { key: 'insights', path: '/your-insights', exact: true, Page: InsightsRedirect },
  { key: 'yourAuditions', path: '/your-insights/your-auditions', exact: true, Page: YourAuditions },
  { key: 'submissionTrends', path: '/your-insights/submission-trends', exact: true, Page: SubmissionTrends },
  { key: 'roleTracker', path: '/your-insights/role-tracker', exact: true, Page: RoleTracker },
  { key: 'editUploadedPhoto', path: '/edit-uploaded-photo', exact: true, Page: EditUploadedPhoto },
  { key: 'notFound', path: '*', Page: NotFound },
] as const);

type AppRouteKey = typeof appRoutes[number]['key'];

export const Routes = (): ReactElement => {
  const submissionTrends = useFeatureExperiment(FEATURE_FLAGS.SUBMISSION_TRENDS);
  const roleTracker = useFeatureExperiment(FEATURE_FLAGS.WEB_ROLE_TRACKER);
  const yourAuditions = useFeatureExperiment(FEATURE_FLAGS.AUDITIONS);
  const headshotsDirectBooking = useFeatureExperiment(FEATURE_FLAGS.WEB_HEADSHOTS_DIRECT_BOOKING);
  useRemoveSplashLoader();
  const { complete } = useCouponCodeCatcher();

  // Flag-gated paths stay in appRoutes so routeHelper can build them. Omit them
  // from the matcher while the flag is off so those URLs still fall through to NotFound.
  const routeDefs = useMemo(() => {
    const hidden = new Set<AppRouteKey>();
    if (!headshotsDirectBooking) hidden.add('headshots');
    if (!yourAuditions) hidden.add('yourAuditions');
    if (!submissionTrends) hidden.add('submissionTrends');
    if (!roleTracker) hidden.add('roleTracker');
    if (hidden.size === 0) return appRoutes;
    return appRoutes.filter((route) => !hidden.has(route.key));
  }, [headshotsDirectBooking, yourAuditions, submissionTrends, roleTracker]);

  if (!complete) {
    return null;
  }

  return <RenderRoutes routeDefs={routeDefs} />;
};

export default Routes;
